import { fromJS, Map, List } from 'immutable';
import { constants as jobCardConstants } from '@saucelabs/sl-component-job-card';

import {
  LS_DASHBOARD_FILTERS,
  FILTER_AUTOMATED_TESTS,
  FILTER_MANUAL_TESTS,
  FILTER_TYPE_PERFORMANCE,
  FILTER_TYPE_OWNERSHIP,
  FILTER_TYPE_STATUS,
  FILTER_TYPE_SEARCH,
  PERFORMANCE_DEFAULT,
} from './constants';

const { PERF_DISPLAY_OPTIONS } = jobCardConstants;

export const serializeFiltersToLS = (payload) => (
  JSON.stringify(payload.toObject())
);

// store default perf option if any invalid perf option is selected
export const validatePerfDropdown = (filters) => {
  const selectedPerfOption = filters.getIn([FILTER_AUTOMATED_TESTS, FILTER_TYPE_PERFORMANCE]);
  return !Object.values(PERF_DISPLAY_OPTIONS).includes(selectedPerfOption) ?
    filters.setIn([FILTER_AUTOMATED_TESTS, FILTER_TYPE_PERFORMANCE], PERFORMANCE_DEFAULT) :
    filters;
};

export const deserializeFiltersFromLS = (filters) => validatePerfDropdown(fromJS(filters));

export const setLocalStorageFilters = (updatedFilters) => {
  window.localStorage.setItem(
    LS_DASHBOARD_FILTERS,
    serializeFiltersToLS(updatedFilters),
  );
};

export const getMyUsernameFilter = (username, id) => ({
  name: username,
  label: `My tests (${username})`,
  type: 'user',
  id,
});

export const getUsernameFilter = (username) => ({
  name: username,
  label: username,
  type: 'user',
});

export const getOrganizationFilter = () => ({
  name: 'organization',
  label: 'My organization',
  type: 'organization',
});

export const getTeamsFilter = (orgTeams, filterByIds) => {
  const mapper = (team) => ({
    id: team.id,
    name: team.id,
    label: team.name,
    type: 'team',
  });

  return filterByIds ?
    orgTeams.toJS().filter((team) => filterByIds.includes(team.id)).map(mapper) :
    orgTeams.toJS().map(mapper);
};

export const getUsersFilter = (orgUsers, loggedInUser) => (
  orgUsers.toJS()
    .filter(({ username }) => loggedInUser !== username)
    .map(({ username, id }) => ({
      name: username,
      label: username,
      id,
      type: 'user',
    }))
);

export const getTeamUsers = (userTeamIds, orgUsers, loggedInUser) => orgUsers.toJS()
  .reduce((acc, user) => {
    if (user.teams?.length && loggedInUser !== user.username) {
      const isInTeam = user.teams.find(({ id }) => userTeamIds.includes(id));

      if (isInTeam) {
        return [
          ...acc,
          {
            name: user.username,
            label: user.username,
            id: user.user_id || user.id,
            type: 'user',
          },
        ];
      }
    }

    return acc;
  }, []);

export const buildOwnerFilter = ({
  // NOTE: jobsTeamSharingEnabled refers to the `jobs_cross_team_sharing` organization setting
  jobsTeamSharingEnabled = false,
  orgTeams,
  orgUsers,
  user,
  username,
}) => {
  const filters = [
    getMyUsernameFilter(username, user.get('user_id')),
  ];

  if (user.isOrgAdmin() || jobsTeamSharingEnabled) {
    filters.push(getOrganizationFilter());
  }
  filters.push(...getUsersFilter(orgUsers, username));
  filters.push(...getTeamsFilter(orgTeams));

  return filters;
};

export const getOwnershipFilter = ({
  filterReference,
  filterType,
  isSubaccountDashboard,
  isOpenSauceUser,
  username,
}) => {
  // We don't want to render filters for tabs which doesnt have a filter key
  if (!filterReference?.get(filterType)) {
    return {};
  }

  if (isSubaccountDashboard || isOpenSauceUser) {
    return getMyUsernameFilter(username);
  }

  const filterResult = filterReference.getIn([filterType, FILTER_TYPE_OWNERSHIP]);

  if (filterResult instanceof Map) {
    return filterResult.toObject();
  } if (typeof filterResult === 'string') {
    return getUsernameFilter(username);
  }

  return {};
};

export const getStatusesFilter = ({ filterReference, filterType }) => (
  filterReference.getIn([filterType, FILTER_TYPE_STATUS], List())
);

export const getSearchFilter = ({ filterReference, filterType }) => (
  filterReference.getIn([filterType, FILTER_TYPE_SEARCH], '')
);

export const noFilterInUse = ({ filters, filterType, ownership, username }) => {
  if (!filters.has(filterType)) {
    return true;
  }

  const status = filters.getIn([filterType, FILTER_TYPE_STATUS]);
  return (
    status.size === 0 &&
    (!ownership.name || ownership.name === username)
  );
};

export const manualFilterSet = ({ filters, owner, username }) => {
  if (!filters.has(FILTER_MANUAL_TESTS)) {
    return false;
  }

  const isOwnerFiltered = owner && owner.type === 'user' && owner.name !== username;
  const statusFilter = filters.getIn([FILTER_MANUAL_TESTS, FILTER_TYPE_STATUS], List());
  const searchFilter = filters.getIn([FILTER_MANUAL_TESTS, FILTER_TYPE_SEARCH], '');

  return isOwnerFiltered || statusFilter.size > 0 || !!searchFilter;
};

export const getUserId = ({
  usersWithIds,
  ownership,
  user,
}) => {
  if (ownership?.id) {
    return ownership.id;
  }

  if (!ownership || usersWithIds.isEmpty() || ownership?.type !== 'user') {
    return undefined;
  }

  if (ownership.name === user?.get('username')) {
    return user.get('user_id');
  }

  const userInfo = usersWithIds.find((entry) => entry.get('username') === ownership.name);
  return userInfo ? userInfo.get('id') : undefined;
};
